// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { NotificationService } from '@way/theme-host/services/notification.service';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import {
  ModernPageHeaderComponent as PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { buildHeaderCtaButton } from '../shared/header-cta.util';
import { ModernPageFooterComponent as PageFooterComponent } from '../shared/footer/footer.component';
import {
  SharedPackageSelectionModalComponent,
  SelectedPackage,
} from '@way/website-themes/themes/shared/package-selection-modal/package-selection-modal.component';
import { PACKAGE_ICON_FALLBACK } from '@way/theme-host/shared/constants/website-builder-assets';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'express-details'
    | 'packages'
    | 'coupons'
    | 'fleets';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-modern-express-auto-details-preview',
  standalone: true,
  providers: [NotificationService],
  imports: [
    CommonModule,
    PageHeaderComponent,
    PageFooterComponent,
    SharedPackageSelectionModalComponent,
  ],
  templateUrl: './express-auto-details-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./express-auto-details-preview.component.scss'],
})
export class ModernExpressAutoDetailsPreviewComponent implements OnInit, OnChanges {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {}; // Section visibility control
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  error: string | null = null;
  readonly shimmerSlots = [0, 1, 2] as const;

  // Page data from JSON
  headerData: any = null;
  pageTitleData: any = null;
  servicesSectionData: any = null;
  footerData: any = null;

  // Package Selection Modal
  showPackageSelectionModal = false;
  selectedPackageForModal: Package | null = null;

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadPageContent();
    this.loadPackages();
  }

  /**
   * Handle input changes for the component, triggering content reloads and navigation updates as needed.
   * @example
   * gOnChanges({ pageData: { firstChange: false } })
   * void
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges object containing changed input properties.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of pageTitleData and servicesSectionData to get updated background color
      const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
      const sections = page?.sections || {};
      this.pageTitleData = sections['page-title'] || {};
      this.servicesSectionData = sections.services_section || {};
    }
  }

  /**
   * Load and parse Express Auto Details page sections from this.pageData into component properties.
   * @example
   * this.loadPageContent()
   * undefined
   * @returns {{void}} Parses page data (supports editor and preview formats), populates headerData, pageTitleData, servicesSectionData, footerData, and updates header navigation; does not return a value.
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('? Loading Express Auto Details page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || {};

    this.headerData = sections.header || {};
    this.pageTitleData = sections['page-title'] || {};
    this.servicesSectionData = sections.services_section || {};
    this.footerData = sections.footer || {};

    this.logger.debug('Express Auto Details sections loaded:', {
      header: this.headerData,
      hero: this.pageTitleData,
      services: this.servicesSectionData,
      footer: this.footerData,
    });

    // Update header navigation to match current pages
    this.updateHeaderNavigation();
  }

  /**
   * Update the component's headerData.navigation from pages and ensure a default CTA button exists.
   * @example
   * this.updateHeaderNavigation()
   * // undefined
   * @returns {void} No return value; updates this.headerData in-place.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Express Auto Details:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Loads detailing packages from the package service, updating the component's packages, loading, and error state.
   * @example
   * loadPackages()
   * // void — updates this.packages, this.loading, and this.error based on the service response
   * @returns {void} No return value; side-effects include setting this.packages, this.loading, and this.error.
   */
  private loadPackages(): void {
    this.loading = true;
    this.error = null;

    this.packageService.getDetailingPackages().subscribe({
      next: response => {
        this.logger.debug('? Detailing packages loaded:', response);
        if (response.status && response.data) {
          this.packages = response.data;
          this.logger.debug('Packages count:', this.packages.length);
        } else {
          this.error = 'No detailing packages available';
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('? Error loading detailing packages:', err);
        this.error = 'Failed to load detailing packages';
        this.loading = false;
      },
    });
  }

  // Format price for display
  formatPrice(price: number | string | undefined): string {
    if (!price) return '$0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  }

  // Get package image or fallback
  getPackageImage(pkg: Package): string {
    // Use image from API if available, otherwise fall back to static placeholder
    if (pkg.image && pkg.image.trim() !== '') {
      return pkg.image;
    }
    return PACKAGE_ICON_FALLBACK;
  }

  // Get package image fit/scaletype
  /**
   * Determine the appropriate CSS object-fit value for a package image based on the package's scaletype in servicesSectionData.
   * @example
   * etPackageImageFit({ id: 'pkg1' })
   * 'cover'
   * @param {{Package}} {{pkg}} - The package object (must include id) to look up scaletype configuration.
   * @returns {{string}} Return one of: 'fill', 'cover', 'contain', 'scale-down' (defaults to 'cover').
   **/
  getPackageImageFit(pkg: Package): string {
    // Check services_section.packages array for this package's scaletype
    const servicesSectionPackages = this.servicesSectionData?.packages || [];
    const packageConfig = servicesSectionPackages.find((p: any) => p.id === pkg.id);

    if (packageConfig?.scaletype) {
      // Map API scaletype to CSS object-fit values
      const scaletype = packageConfig.scaletype;
      if (scaletype === 'fill' || scaletype === 'stretch') {
        return 'fill';
      } else if (scaletype === 'cover') {
        return 'cover';
      } else if (scaletype === 'contain') {
        return 'contain';
      } else if (scaletype === 'fit') {
        return 'scale-down';
      }
    }

    // Default to cover if not specified
    return 'cover';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = PACKAGE_ICON_FALLBACK;
  }

  // Get display price
  getListPrice(pkg: Package): string {
    return pkg.single_list_price;
  }

  // Get strikeoff price (discounted price)
  getStrikeoffPrice(pkg: Package): string {
    return pkg.type === 'unlimited' ? pkg.monthly_strikeoff_price : pkg.single_strikeoff_price;
  }

  isUnlimited(pkg: Package): boolean {
    return pkg.type === 'unlimited' || pkg.package_name?.toLowerCase().includes('unlimited');
  }

  getPackageButtonText(pkg: Package): string {
    const servicesElement = this.elements.find(
      e =>
        e.id === 'express-details' ||
        e.type === 'express-details' ||
        (e as any).originalKey === 'services_section'
    );
    if (servicesElement?.content?.packages) {
      const elementPackage = servicesElement.content.packages.find((p: any) => p.id === pkg.id);
      if (elementPackage?.buttonText) {
        return elementPackage.buttonText;
      }
    }

    const servicesSectionPackages = this.servicesSectionData?.packages || [];
    const packageConfig = servicesSectionPackages.find((p: any) => p.id === pkg.id);
    if (packageConfig?.buttonText) {
      return packageConfig.buttonText;
    }

    return 'Buy Now';
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  /**
   * Retrieve the header logo object from the component's header content, returning defaults if not present.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Logo', link: '/' }
   * @returns {{Logo}} Logo object containing image, text and link properties.
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.logo) {
      return {
        image: headerContent.logo.image || headerContent.logo.url || '',
        text: headerContent.logo.text || 'Logo',
        link: headerContent.logo.link || '/',
      };
    }
    return { text: 'Logo', link: '/' };
  }

  /**
   * Returns the header navigation items for the preview, using header content navigation if available, otherwise deriving items from visible pages, or an empty array.
   * @example
   * etHeaderNavigation()
   * [{ label: 'Overview', link: '#overview', active: false }]
   * @returns {NavigationItem[]} Array of navigation items for the header.
   */
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      return headerContent.navigation;
    }
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Retrieve the CTA button configuration from the header content, falling back to sensible defaults.
   * @example
   * etHeaderCTAButton()
   * // returns { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @returns {CTAButton|null} CTAButton object derived from the header content or a default CTAButton.
   */
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    return buildHeaderCtaButton(headerContent?.ctaButton);
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Get footer quick links, using configured quickLinks if available, otherwise derive from visible pages.
   * @example
   * etFooterQuickLinks()
   * [{ label: 'Home', link: '#home' }, { label: 'Contact', link: '#contact' }]
   * @returns {{Array<{ label: string; link: string }>}} Array of link objects with label and link properties.
   **/
  getFooterQuickLinks(): { label: string; link: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Extracts and normalizes social links from the component's footer content.
   * @example
   * this.etFooterSocialLinks()
   * [{ platform: 'facebook', url: 'https://facebook.com/example', icon: 'facebook' }]
   * @returns {Array<{ platform: string; url: string; icon: string }>} An array of social link objects with normalized platform, url, and icon properties.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Get the footer logo object from the footer element, falling back to the header logo or a default.
   * @example
   * etFooterLogo()
   * { text: 'way', image: 'https://example.com/logo.png' }
   * @returns {{ text?: string; image?: string } | undefined} Return an object containing optional text and image for the logo, or undefined if not available.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  onHeaderClick(): void {
    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  onFooterClick(event: Event): void {
    event.stopPropagation();
    const footerElement = this.getFooterElement();
    if (footerElement) {
      if (!footerElement.type || footerElement.type !== 'footer') {
        footerElement.type = 'footer';
      }
      if (!footerElement.id || footerElement.id !== 'footer') {
        footerElement.id = 'footer';
      }
      this.onElementClick(footerElement);
    }
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#1E3A8A';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#64748B';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Inter';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get hero title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Express Auto Details – Shine in No Time!';
  }

  /**
   * Get hero subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return "Whether it's a quick clean or a full headlamp refresh, our Express Detail Packages give your ride that 'like-new' look without the wait.";
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // Check page-level title first (highest priority)
    const page = this.pageData?.page || this.pageData;
    if (page?.title && typeof page.title === 'object' && page.title.style?.text_style) {
      const titleStyleObj = page.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }

    // Fallback to section-level title
    if (this.pageTitleData?.title && typeof this.pageTitleData.title === 'object') {
      const title = this.pageTitleData.title;
      const titleStyleObj = title.style?.text_style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }

    return {};
  }

  /**
   * Get hero section element for selection
   */
  getPageHeaderSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'express-details' ||
        el.id === 'express-details' ||
        (el as any).originalKey === 'page-title'
    );
  }

  /**
   * Handle hero section click
   */
  /**
   * Get page header section background color
   */
  getPageHeaderSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const pageHeaderElement = this.getPageHeaderSectionElement();
    const pageHeaderContent = pageHeaderElement?.content as any;
    if (pageHeaderContent?.backgroundColor) {
      return pageHeaderContent.backgroundColor;
    }
    // Priority 2: Check pageTitleData (from API/pageData)
    if (this.pageTitleData?.backgroundColor) {
      return this.pageTitleData.backgroundColor;
    }
    // Priority 3: Check servicesSectionData (from API/pageData)
    if (this.servicesSectionData?.backgroundColor) {
      return this.servicesSectionData.backgroundColor;
    }
    // Priority 4: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 5: Check pageData directly
    const sections = page?.sections || {};
    const pageTitleSection = sections['page-title'] || {};
    if (pageTitleSection.backgroundColor) {
      return pageTitleSection.backgroundColor;
    }
    const servicesSection = sections.services_section || {};
    if (servicesSection.backgroundColor) {
      return servicesSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get services section background color (same as page header for unified look)
   */
  getServicesSectionBackgroundColor(): string | null {
    return this.getPageHeaderSectionBackgroundColor();
  }

  /**
   * Handles a click on the page header (hero) section, constructs a WebsiteElement representing the hero with title, subtitle and background color, logs it, and emits it via the elementSelect EventEmitter.
   * @example
   * nPageHeaderSectionClick(new Event('click'))
   * undefined
   * @param {{Event}} {{event}} - The click Event triggered by the user interacting with the page header section.
   * @returns {{void}} Does not return a value; emits the constructed WebsiteElement through elementSelect.
   **/
  onPageHeaderSectionClick(event: Event): void {
    event.stopPropagation();
    const heroElement: WebsiteElement = {
      id: 'express-details',
      type: 'express-details',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        backgroundColor: this.getPageHeaderSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'page-title',
    } as any;
    this.logger.debug('✨ Hero section clicked, emitting element:', heroElement);
    this.elementSelect.emit(heroElement);
  }

  /**
   * Handle services section click
   */
  onServicesSectionClick(event: Event): void {
    event.stopPropagation();
    const servicesElement: WebsiteElement = {
      id: 'services',
      type: 'services',
      content: {
        ...(this.servicesSectionData || {}),
        backgroundColor: this.getServicesSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'services_section',
    } as any;
    this.elementSelect.emit(servicesElement);
  }

  getServicesSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'services' ||
        el.id === 'services' ||
        (el as any).originalKey === 'services_section'
    );
  }

  onBuyNowClick(pkg: Package, event: Event): void {
    event.stopPropagation();
    this.selectedPackageForModal = pkg;
    this.showPackageSelectionModal = true;
  }

  onClosePackageModal(): void {
    this.showPackageSelectionModal = false;
    this.selectedPackageForModal = null;
  }

  onPackageSelected(selection: SelectedPackage): void {
    this.logger.debug('Package selected:', selection);
  }

  getPackagesForModal(): Package[] {
    if (this.selectedPackageForModal) {
      return [this.selectedPackageForModal];
    }
    return this.packages;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
